import {inject, Injectable, Signal, signal, WritableSignal} from "@angular/core";
import {OrderPaymentDetailsData} from '../payment/order-payment-details-entity.model';
import {IntegratedPaymentProvider, PaymentType} from '../payment/payment';
import {ConfigurationModel} from '../configuration/configuration.model';

export class CustomerDetails {
    public firstName: string;
    public lastName: string;
    public email: string;
    public mobilePhone: string;
    public marketingOptInDecided: boolean;
    public lastPaymentType: PaymentType;
    public version: number;

    public get populated(): boolean {
        return !!this.firstName || !!this.lastName || !!this.email || !!this.mobilePhone;
    }
}

export class PaymentCustomerDetails {
    public paymentCustomerId: string;
    public created: Date = new Date();
}

/**
 * This service provides storage and retrieval of customer details to and from local device storage.
 */
@Injectable({providedIn: "root"})
export class CustomerDetailsService {
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);

    private static readonly CUSTOMER_STORAGE_KEY: string = "gd";
    private static readonly CUSTOMER_STORAGE_VERSION: number = 2;
    private static readonly PAYMENT_CUSTOMER_STORAGE_KEY_PREFIX: string = "gpc";

    private paymentCustomerStorageKeyQualifier: string;

    private readonly _hasSavedPaymentCustomer: WritableSignal<boolean> = signal<boolean>(false);
    public readonly hasSavedPaymentCustomer: Signal<boolean> = this._hasSavedPaymentCustomer.asReadonly();

    constructor() {
        console.log("--- CustomerDetailsService");

        // Listen for storage changes from other windows/tabs
        window.addEventListener('storage', (event: StorageEvent) => {
            if (event.key && event.key.startsWith(CustomerDetailsService.PAYMENT_CUSTOMER_STORAGE_KEY_PREFIX)) {
                this.updateHasSavedPaymentCustomer();
            }
        });
    }

    public static toBase64(s: string): string {
        // @ts-ignore
        return btoa(Array.from(new TextEncoder().encode(s), (x: number) => String.fromCodePoint(x)).join(""));
    }

    public static fromBase64(s: string): string {
        // @ts-ignore
        return new TextDecoder().decode(Uint8Array.from(atob(s), (x: number) => x.codePointAt(0)));
    }

    public saveCustomer(customerDetails: CustomerDetails): CustomerDetails {
        customerDetails.version = CustomerDetailsService.CUSTOMER_STORAGE_VERSION;
        localStorage.setItem(CustomerDetailsService.CUSTOMER_STORAGE_KEY, CustomerDetailsService.toBase64(JSON.stringify(customerDetails)));
        return customerDetails;
    }

    public loadCustomer(): CustomerDetails {
        let customerDetails: CustomerDetails = new CustomerDetails();
        if (this.configurationModel.isWaiterActor()) {
            return customerDetails;
        }
        const data: string = localStorage.getItem(CustomerDetailsService.CUSTOMER_STORAGE_KEY);
        if (data) {
            Object.assign(customerDetails, JSON.parse(CustomerDetailsService.fromBase64(data)));
            if (customerDetails.version !== CustomerDetailsService.CUSTOMER_STORAGE_VERSION) {
                customerDetails = new CustomerDetails();
                this.removeCustomer();
            }
        }
        customerDetails.version = CustomerDetailsService.CUSTOMER_STORAGE_VERSION;
        return customerDetails;
    }

    // noinspection JSUnusedGlobalSymbols
    public removeCustomer(): void {
        localStorage.removeItem(CustomerDetailsService.CUSTOMER_STORAGE_KEY);
    }

    public setPaymentScope(paymentDetails: OrderPaymentDetailsData) {
        this.paymentCustomerStorageKeyQualifier = (paymentDetails.provider === IntegratedPaymentProvider.STRIPE ? "" : ("." + paymentDetails.provider)) + (paymentDetails.platformLiveMode ? "" : ("." + "test"));
        this.updateHasSavedPaymentCustomer();
    }

    private get paymentCustomerStorageKey(): string {
        return CustomerDetailsService.PAYMENT_CUSTOMER_STORAGE_KEY_PREFIX + this.paymentCustomerStorageKeyQualifier;
    }

    public savePaymentCustomer(paymentCustomerDetails: PaymentCustomerDetails): void {
        localStorage.setItem(this.paymentCustomerStorageKey, CustomerDetailsService.toBase64(JSON.stringify(paymentCustomerDetails)));
        this.updateHasSavedPaymentCustomer();
    }

    private updateHasSavedPaymentCustomer(): void {
        this._hasSavedPaymentCustomer.set(!!localStorage.getItem(this.paymentCustomerStorageKey));
    }

    public loadPaymentCustomer(): PaymentCustomerDetails {
        const data: string = localStorage.getItem(this.paymentCustomerStorageKey);
        return data ? JSON.parse(CustomerDetailsService.fromBase64(data)) : undefined;
    }

    public removePaymentCustomer(): void {
        localStorage.removeItem(this.paymentCustomerStorageKey);
        this.updateHasSavedPaymentCustomer();
    }
}
